/**
 * The decision half of the mobile-login pairing poll.
 *
 * The component owns the timers, the fetch and the abort controller; this owns
 * what a given response *means* — which view to show, whether to keep polling,
 * and whether the sign-in actually completed. Keeping that separable matters
 * because the failure modes are the interesting part: a transient 500 must not
 * look like an expiry, and only a genuine `redeemed` may fire success.
 */

export type Phase = 'loading' | 'qr' | 'code' | 'success' | 'expired' | 'error';

export interface PollOutcome {
  /** Phase to move to, or null to leave the current one alone. */
  phase: Phase | null;
  stopPolling: boolean;
  emitSuccess: boolean;
  /** The 6-digit code, present only once the pairing has been scanned. */
  code: string | null;
}

const KEEP_POLLING: PollOutcome = { phase: null, stopPolling: false, emitSuccess: false, code: null };

export function resolvePollOutcome(httpStatus: number, body: any): PollOutcome {
  // The pairing is gone — expired, or consumed past its success window.
  if (httpStatus === 404) {
    return { phase: 'expired', stopPolling: true, emitSuccess: false, code: null };
  }

  // Anything else non-OK is treated as transient: the phase is left untouched
  // and the chain continues, so a blip mid-sign-in doesn't throw the user out.
  // The caller's own expiry guard is what eventually ends it.
  if (httpStatus < 200 || httpStatus >= 300) return { ...KEEP_POLLING };

  switch (body?.status) {
    case 'scanned':
      // Reveal the code, but keep polling — redemption still has to happen.
      return { phase: 'code', stopPolling: false, emitSuccess: false, code: body?.code ?? null };
    case 'redeemed':
      return { phase: 'success', stopPolling: true, emitSuccess: true, code: null };
    case 'pending':
    default:
      // An unrecognised status is treated as pending rather than as an error:
      // showing the QR is the safe view to sit on.
      return { phase: 'qr', stopPolling: false, emitSuccess: false, code: null };
  }
}
